import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  fetchTruck7Listings,
  formatPrice,
  formatRegistration,
  getCountryFlag,
} from "../../services/truck7Service";

const BannerWrapper = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-height: calc(100vh - 160px);
  overflow-y: auto;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const BannerHeader = styled.div`
  padding: 1.25rem 1.25rem 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  text-align: center;

  .truck7-logo {
    display: inline-flex;
    align-items: center;
    background: #ff6b35;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    font-weight: 700;
    font-size: 1rem;
    text-decoration: none;
    margin-bottom: 0.75rem;

    &:hover {
      background: #e55a2b;
      color: white;
      text-decoration: none;
    }
  }

  h4 {
    color: #1e293b;
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    line-height: 1.3;
  }

  p {
    color: #64748b;
    font-size: 0.8rem;
    margin: 0;
    line-height: 1.4;
  }
`;

const ListingsContainer = styled.div`
  padding: 0;
`;

const ListingCard = styled.a`
  display: block;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    text-decoration: none;
    color: inherit;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ListingImage = styled.div`
  width: 100%;
  height: 140px; /* Increased height to better accommodate 16:9 images */
  background-image: url(${(props) => props.src});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  border-radius: 6px;
  margin-bottom: 0.75rem;
`;

const ListingTitle = styled.h5`
  font-size: 0.8rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ListingMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

const MetaItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;
  color: #64748b;

  .label {
    font-weight: 500;
  }

  .value {
    font-weight: 600;
    color: #374151;
  }
`;

const PriceTag = styled.div`
  background: #ff6b35;
  color: white;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.8rem;
  text-align: center;
`;

const CountryFlag = styled.span`
  font-size: 0.9rem;
  margin-right: 0.25rem;
`;

const LoadingState = styled.div`
  padding: 2rem 1.25rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
`;

const ErrorState = styled.div`
  padding: 1.25rem;
  text-align: center;
  color: #ef4444;
  background: #fef2f2;
  border-radius: 8px;
  margin: 1.25rem;
  font-size: 0.9rem;

  a {
    color: #ff6b35;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ViewAllButton = styled.a`
  display: block;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  color: #ff6b35;
  text-align: center;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #e55a2b;
    text-decoration: none;
  }
`;

const Truck7VerticalBanner = ({ limit = 5 }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadListings = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await fetchTruck7Listings(limit);
        setListings(data);
      } catch (err) {
        console.error("Error loading Truck7 listings:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadListings();
  }, [limit]);

  if (loading) {
    return (
      <BannerWrapper>
        <LoadingState>Loading vehicles...</LoadingState>
      </BannerWrapper>
    );
  }

  if (error || !listings || listings.length === 0) {
    return (
      <BannerWrapper>
        <BannerHeader>
          <div>
            <a
              href="https://truck7.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="truck7-logo"
            >
              Truck7
            </a>
          </div>
        </BannerHeader>
        <ErrorState>
          <p>Unable to load vehicles</p>
          <a href="https://truck7.eu" target="_blank" rel="noopener noreferrer">
            Visit Truck7.eu →
          </a>
        </ErrorState>
      </BannerWrapper>
    );
  }

  return (
    <BannerWrapper>
      <BannerHeader>
        <div>
          <a
            href="https://truck7.eu"
            target="_blank"
            rel="noopener noreferrer"
            className="truck7-logo"
          >
            Truck7
          </a>
        </div>
        <h4>Featured Vehicles</h4>
      </BannerHeader>

      <ListingsContainer>
        {listings.map((listing) => (
          <ListingCard
            key={listing.id}
            href={listing.public_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListingImage
              src={
                listing.images && listing.images.length > 0
                  ? listing.images[0]
                  : ""
              }
            />

            <ListingTitle>{listing.listing_title}</ListingTitle>

            <ListingMeta>
              <MetaItem>
                <span className="label">Location:</span>
                <span className="value">
                  <CountryFlag>{getCountryFlag(listing.country)}</CountryFlag>
                  {listing.country}
                </span>
              </MetaItem>

              {listing.attributes?.first_registration_year && (
                <MetaItem>
                  <span className="label">Year:</span>
                  <span className="value">
                    {formatRegistration(listing.attributes)}
                  </span>
                </MetaItem>
              )}
            </ListingMeta>

            <PriceTag>{formatPrice(listing.price)}</PriceTag>
          </ListingCard>
        ))}
      </ListingsContainer>

      <ViewAllButton
        href="https://truck7.eu"
        target="_blank"
        rel="noopener noreferrer"
      >
        View All Vehicles →
      </ViewAllButton>
    </BannerWrapper>
  );
};

export default Truck7VerticalBanner;
